import { Box, Stack } from '@mui/material';
import { FC } from 'react';

import { processCompDataValue, ReadonlyField } from './helpers';
import { ReadonlySection } from './section';
import ReadonlyPair from './readonly-pair';

interface Props {
  fields: ReadonlyField[];
  data: Record<string, unknown>;
  onData?: (data: Record<string, unknown>) => Record<string, unknown>;
}

const ReadonlyView: FC<Props> = ({ fields, data = {}, onData }) => {
  const compData = { ...(onData ? onData(data) : data) };

  // Group fields by rows
  const rows: ReadonlyField[][] = [];
  let currentRow: ReadonlyField[] = [];

  fields.forEach((field, index) => {
    currentRow.push(field);
    const nextField = fields[index + 1];
    const { rowGroup } = field;
    const hasRowGroup = !!rowGroup;
    const shouldBreak =
      !hasRowGroup || (nextField && rowGroup !== nextField.rowGroup) || index === fields.length - 1;

    if (shouldBreak) {
      rows.push(currentRow);
      currentRow = [];
    }
  });

  // Render a single field
  const renderField = ({ label, prop, propType }: ReadonlyField) => {
    if (propType === 'section') {
      return <ReadonlySection key={`${prop}-section`} label={label} />;
    }

    const rawVal = compData[prop];
    const propVal = processCompDataValue(rawVal, propType);

    if (!propVal) {
      return null;
    }

    return <ReadonlyPair key={prop} label={label} propVal={propVal} />;
  };

  return (
    <Stack width="100%">
      {rows.map((fieldRow, rowIndex) => {
        const renderedFields = fieldRow.map(renderField);

        // Single field - full width
        if (fieldRow.length === 1) {
          return (
            <Box key={fieldRow[0].prop} width="100%" mb={2}>
              {renderedFields[0]}
            </Box>
          );
        }

        // Multiple fields - horizontal layout
        return (
          <Stack key={rowIndex} direction="row" flexWrap="wrap" gap={8} mb={2}>
            {renderedFields.map((field, index) => (
              <Box key={fieldRow[index].prop} minWidth={0}>
                {field}
              </Box>
            ))}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default ReadonlyView;
